import React, { memo, useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { Dispatch } from 'react';

import { token } from '@atlaskit/tokens';

import { TaskView } from '../tasksDefinitions.ts';
import type { TaskPages, TaskSpaces } from '../tasksDefinitions.ts';

import { SelectDueDate } from './SelectDueDate.tsx';
import { SelectCompletion } from './SelectCompletion.tsx';
import { SelectCreator } from './SelectCreator.tsx';
import { SelectAssignee } from './SelectAssignee.tsx';
import { SelectPage } from './SelectPage.tsx';
import { SelectSpace } from './SelectSpace.tsx';
import {
	FilterHeaderCompiled,
	InputContainerCompiled,
	SelectContainerCompiled,
	ClearButtonCompiled,
} from './presentationalComponents.tsx';
import type { FiltersState } from './useFiltersReducer.ts';
import { FilterKeys } from './filtersDefinitions.ts';

const filtersContainerStyles = css({
	padding: token('space.150'),
	width: 400,
});

const clearContainerStyles = css({
	display: 'inline-block',
	width: '100%',
	textAlign: 'right',
});

const i18n = defineMessages({
	filterLabel: {
		id: 'home.views.tasks.filters.label.filterLabel',
		defaultMessage: 'FILTER',
		description: 'The header text for the filter label column that lists the filter names.',
	},
	clearFilters: {
		id: 'home.views.tasks.filters.label.clearFilters',
		defaultMessage: 'Clear',
		description: "The text for the clear filters button who's action is to clear filters.",
	},
});

interface FilterFormProps {
	selectedTableView: TaskView;
	options?: {
		pages: TaskPages;
		spaces: TaskSpaces;
	};
	filterDispatch: Dispatch<any>;
	filterState: FiltersState;
}

export const FiltersForm = memo(
	({
		selectedTableView,
		filterDispatch,
		options = { pages: {}, spaces: {} },
		filterState,
	}: FilterFormProps) => {
		const { formatMessage } = useIntl();
		const [node, setNode] = useState<Element | null>(null);

		// Use this for now to override styles while we wait for changes in design system.
		// TODO: replace this when updated.
		useEffect(() => {
			const node = document.querySelector('[id^="ds--dropdown--"]');
			if (node) {
				setNode(node);
			}
		}, []);

		useEffect(() => {
			if (node) {
				(node as HTMLElement).style.overflow = 'visible';
				const menu = node.querySelector('div[role="menu"]');

				if (menu) {
					(menu as HTMLElement).style.overflow = 'visible';
				}
			}
		}, [node]);

		return (
			<div css={filtersContainerStyles} data-testid="filters-container">
				<InputContainerCompiled>
					<FilterHeaderCompiled>{formatMessage(i18n.filterLabel)}</FilterHeaderCompiled>
					<SelectContainerCompiled>
						<div css={clearContainerStyles}>
							<ClearButtonCompiled onClick={() => filterDispatch({ type: FilterKeys.DEFAULT })}>
								{formatMessage(i18n.clearFilters)}
							</ClearButtonCompiled>
						</div>
					</SelectContainerCompiled>
				</InputContainerCompiled>
				<SelectDueDate filterDispatch={filterDispatch} filterState={filterState} />
				<SelectCompletion filterState={filterState} filterDispatch={filterDispatch} />
				{selectedTableView === TaskView.Assigned ? (
					<SelectCreator filterState={filterState} filterDispatch={filterDispatch} />
				) : (
					<SelectAssignee filterState={filterState} filterDispatch={filterDispatch} />
				)}
				<SelectPage
					options={options.pages}
					filterDispatch={filterDispatch}
					filterState={filterState}
				/>
				<SelectSpace
					options={options.spaces}
					filterDispatch={filterDispatch}
					filterState={filterState}
				/>
			</div>
		);
	},
);
